/**
 * 预览 iframe 的"拖选元素"模式 (移植自 legacy/js/template-editor.js 的注入脚本)。
 *
 * 与 legacy 的差异:
 *  - iframe 只回传选中元素的 DOM 路径 (children 下标序列), 片段替换在父页对
 *    HTML 源码做 (DOMParser), 不再从 iframe 回传整页 outerHTML ——
 *    这样源码里永远不会混入辅助脚本 / 高亮 class / __select_mode__ class。
 *  - 父页校验 e.source, 只接收自己 iframe 发来的消息。
 *
 * postMessage 协议
 *  父 → iframe: { type: "set-select-mode", on }      切换拖选模式
 *               { type: "highlight-path", path|null } 重新高亮 (iframe 重新加载后)
 *  iframe → 父: { type: "selection-ready", path, html } 选中了一个元素
 */

export type FrameToParent = { type: "selection-ready"; path: number[]; html: string };
export type ParentToFrame =
  | { type: "set-select-mode"; on: boolean }
  | { type: "highlight-path"; path: number[] | null };

const HELPER = `
<style id="__editor_helper_style">
  .__sel_overlay__ { position: fixed; background: rgba(102,126,234,0.18); border: 2px dashed #5e72e4; pointer-events: none; z-index: 99998; }
  .__sel_highlight__ { outline: 3px solid #5e72e4 !important; outline-offset: 2px; background: rgba(102,126,234,0.08) !important; }
  html.__select_mode__, html.__select_mode__ * { cursor: crosshair !important; }
</style>
<script id="__editor_helper_script">
(function(){
  var root = document.documentElement;
  var overlay = null, startX = 0, startY = 0, dragging = false, last = null;

  function send(msg){ window.parent.postMessage(msg, '*'); }
  function clearHighlight(){ if (last) { last.classList.remove('__sel_highlight__'); last = null; } }
  function isHelper(el){ return !el || el === root || el === document.body || el.id === '__editor_helper_style' || el.id === '__editor_helper_script'; }

  function pathOf(el){
    var p = [];
    while (el && el !== root) {
      var parent = el.parentElement;
      if (!parent) return null;
      p.unshift(Array.prototype.indexOf.call(parent.children, el));
      el = parent;
    }
    return p;
  }
  function byPath(p){
    var el = root;
    for (var i = 0; i < p.length; i++) { el = el && el.children[p[i]]; }
    return el || null;
  }

  // 完全包含矩形且最深的节点 (elementsFromPoint 由深到浅); 找不到则取四角 LCA
  function findEnclosingNode(r){
    var cands = document.elementsFromPoint(r.x + r.w/2, r.y + r.h/2);
    for (var i = 0; i < cands.length; i++) {
      var el = cands[i];
      if (isHelper(el) || el.classList.contains('__sel_overlay__')) continue;
      var b = el.getBoundingClientRect();
      if (b.left <= r.x && b.top <= r.y && b.right >= r.x + r.w && b.bottom >= r.y + r.h) return el;
    }
    var tl = document.elementFromPoint(r.x, r.y);
    var br = document.elementFromPoint(r.x + r.w, r.y + r.h);
    if (!tl || !br) return null;
    var a = tl;
    while (a && !a.contains(br)) a = a.parentElement;
    return a || tl;
  }

  function select(el){
    if (isHelper(el)) return;
    var p = pathOf(el);
    if (!p) return;
    var html = el.outerHTML;            // 先取 outerHTML, 再加高亮 class
    last = el;
    el.classList.add('__sel_highlight__');
    send({ type: 'selection-ready', path: p, html: html });
  }

  window.addEventListener('message', function(e){
    var d = e.data || {};
    if (d.type === 'set-select-mode') {
      root.classList.toggle('__select_mode__', !!d.on);
    } else if (d.type === 'highlight-path') {
      clearHighlight();
      if (d.path) { var el = byPath(d.path); if (el && !isHelper(el)) { last = el; el.classList.add('__sel_highlight__'); } }
    }
  });

  document.addEventListener('mousedown', function(e){
    if (!root.classList.contains('__select_mode__') || e.button !== 0) return;
    e.preventDefault();
    clearHighlight();
    dragging = true;
    startX = e.clientX; startY = e.clientY;
    overlay = document.createElement('div');
    overlay.className = '__sel_overlay__';
    overlay.style.left = startX + 'px';
    overlay.style.top = startY + 'px';
    document.body.appendChild(overlay);
  }, true);

  document.addEventListener('mousemove', function(e){
    if (!dragging || !overlay) return;
    overlay.style.left = Math.min(startX, e.clientX) + 'px';
    overlay.style.top = Math.min(startY, e.clientY) + 'px';
    overlay.style.width = Math.abs(e.clientX - startX) + 'px';
    overlay.style.height = Math.abs(e.clientY - startY) + 'px';
  }, true);

  document.addEventListener('mouseup', function(e){
    if (!dragging) return;
    dragging = false;
    var rect = overlay.getBoundingClientRect();
    overlay.remove(); overlay = null;
    if (rect.width < 5 || rect.height < 5) {
      select(document.elementFromPoint(e.clientX, e.clientY));   // 点击即选中
      return;
    }
    var node = findEnclosingNode({ x: rect.left, y: rect.top, w: rect.width, h: rect.height });
    if (node) select(node);
  }, true);

  // 拖选模式下屏蔽链接 / 按钮的点击
  document.addEventListener('click', function(e){
    if (root.classList.contains('__select_mode__')) { e.preventDefault(); e.stopPropagation(); }
  }, true);
})();
<\/script>
`;

/** 在 </body> 前注入辅助脚本 (仅用于 iframe 预览, 不写回源码) */
export function injectSelectHelper(html: string): string {
  if (/<\/body>/i.test(html)) return html.replace(/<\/body>(?![\s\S]*<\/body>)/i, HELPER + "</body>");
  return html + HELPER;
}

export function isFrameMessage(d: unknown): d is FrameToParent {
  if (!d || typeof d !== "object") return false;
  const m = d as Record<string, unknown>;
  return m.type === "selection-ready" && Array.isArray(m.path) && typeof m.html === "string";
}

function parse(html: string): Document {
  return new DOMParser().parseFromString(html, "text/html");
}

function byPath(doc: Document, path: number[]): Element | null {
  let el: Element | null = doc.documentElement;
  for (const i of path) {
    el = el?.children[i] ?? null;
    if (!el) return null;
  }
  return el === doc.documentElement ? null : el;
}

function serialize(doc: Document, original: string): string {
  const doctype = /^\s*<!doctype/i.test(original) ? "<!DOCTYPE html>\n" : "";
  return doctype + doc.documentElement.outerHTML;
}

/** 根据路径从源码里取出元素的 outerHTML (源码版本, 不含运行时改动) */
export function fragmentAt(html: string, path: number[]): string | null {
  return byPath(parse(html), path)?.outerHTML ?? null;
}

/** 把 path 处的元素替换为 fragment 的第一个元素, 返回新的整页 HTML; 失败返回 null */
export function spliceFragment(html: string, path: number[], fragment: string): string | null {
  const doc = parse(html);
  const target = byPath(doc, path);
  if (!target) return null;
  const tpl = doc.createElement("template");
  tpl.innerHTML = fragment.trim();
  const node = tpl.content.firstElementChild;
  if (!node) return null;
  target.replaceWith(node);
  return serialize(doc, html);
}

/** 选中片段的显示名: data-edit 优先, 否则 <tag> */
export function fragmentLabel(fragment: string): string {
  const dataEdit = fragment.match(/data-edit="([^"]+)"/)?.[1];
  if (dataEdit) return dataEdit;
  const tag = fragment.match(/^\s*<\s*([a-zA-Z][\w-]*)/)?.[1] ?? "?";
  return `<${tag}>`;
}

/** 跨元素布局类指令: 局部改写通常无效 (legacy 关键词启发式) */
const LAYOUT_KEYWORDS = [
  "向右", "向左", "向上", "向下", "挪", "移动", "列宽", "对齐",
  "间距", "靠近", "远离", "贴", "居中", "并排", "换行", "排版",
];

export function looksLikeLayoutChange(instruction: string): boolean {
  return LAYOUT_KEYWORDS.some((k) => instruction.includes(k));
}
